import { createRng } from '../../../core/rng';
import type { Rng } from '../../../core/rng';
import type { MixOptions } from '../kit';
import { Grid, hits } from '../song';
import type { BarInfo, Note, Section } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, midi, noteName, pc, pcs, phrase, sizeOf, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

export type Chord = { root: string; pad: readonly string[] };

export type Bass = readonly (readonly [step: number, semis: number, len: number, vel: number])[];

export type Bar = BarInfo & {
  i: number;
  end: boolean;
  chord: Chord;
  pad: readonly string[];
  bells: readonly string[];
  drop: boolean;
  groove: boolean;
};

export type Ctx = {
  g: Grid;
  s: number;
  e: number;
  key: number;
  form: readonly Section[];
  intro: number;
  total: number;
  drop1: number;
  drop2: number | undefined;
  brk: number | undefined;
  rng: Rng;
  tr(n: string): string;
  hit(v: string, bar: number, step: number, vel: number): void;
  play(v: string, bar: number, step: number, note: string, dur: number, vel: number): void;
  chord(v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number, strum?: number): void;
  push(e: Ev): void;
};

export type Flavor = {
  id: string;
  genre: string;
  bpm: readonly [min: number, max: number];
  ref: Template['ref'];
  chords: Readonly<Record<string, Chord>>;
  progs: readonly (readonly string[])[];
  hook: readonly Note[][];
  arps: readonly (readonly number[])[];
  keys: readonly number[];
  scale: readonly number[];
  range: readonly [number, number];
  center: number;
  plan: FormPlan;
  swing: number;
  lufs: number;
  mix(bpm: number): MixOptions;
  kick: { groove: string; drop: string };
  back: { groove: string; drop: string; vel: readonly [groove: number, drop: number] };
  hats: readonly string[];
  open: { pattern: string; from: number };
  bass: { groove: Bass; drop: Bass };
  arp: { groove: string; drop: string; len: number };
  bells(pad: readonly string[]): string[];
  harm?: number;
  extra?(x: Ctx, b: Bar): void;
  post?(x: Ctx): void;
  accents: Score['accents'];
};

export function below(note: string, scale: readonly number[], steps: number): string {
  let m = midi(note);
  let n = 0;
  while (n < steps) {
    m--;
    if (scale.includes(pc(m))) n++;
  }
  return noteName(m);
}

export const DREAM_PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'A', bars: 8, min: 4, rank: 2 },
    { name: 'drop', bars: 8, min: 4, rank: 1 },
    { name: 'break', bars: 4, min: 4, rank: 4, with: 'drop2' },
    { name: 'drop2', bars: 8, min: 4, rank: 3 },
  ],
};

function build(f: Flavor, tpl: Template, seed: string, p: Params): Score {
  const ch = chooser(tpl, seed);
  const key = ch.pick(f.keys);
  const prog = ch.pick(f.progs).map((n) => f.chords[n]!);
  const arp = ch.pick(f.arps);
  const hatP = ch.pick(f.hats);
  const hook: readonly (readonly Note[])[] = ch.orig
    ? f.hook
    : phrase(
        ch.rng,
        f.hook.map(cellsOf),
        prog.map((c) => [{ at: 0, tones: pcs([c.root, ...c.pad]) }]),
        f.scale,
        f.range,
        f.center,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);

  const form = fitForm(barsOf(p), f.plan);
  const g = new Grid(p.bpm, f.swing, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const drop1 = find(form, 'drop')!;
  const drop2 = find(form, 'drop2');
  const brk = find(form, 'break');
  const rng = createRng(`${f.id}-${seed}`);
  const human = (v: number) => v * (0.93 + rng.float() * 0.14);

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, vel: number) => events.push({ v, t: g.at(bar, step), bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number, strum = 0) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes, strum });
  const x: Ctx = {
    g,
    s,
    e,
    key,
    form,
    intro,
    total,
    drop1,
    drop2,
    brk,
    rng: createRng(`${f.id}-x-${seed}`),
    tr,
    hit,
    play,
    chord,
    push: (ev) => events.push(ev),
  };

  const autos: Score['autos'] = [
    { v: 'padLp', points: [[g.at(0), 450], [g.at(intro), 2600]] },
    { v: 'padLp', points: [[g.at(drop1), 3800], [g.at(drop1 + 1), 3800]] },
    { v: 'padLp', points: [[g.at(total - 2), 3000], [g.at(total), 700]] },
  ];
  if (brk !== undefined) autos.push({ v: 'padLp', points: [[g.at(brk), 1400], [g.at(brk + sizeOf(form, 'break')), 3800]] });

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro, 4);
    const end = b.bar === total - 1;
    const c = end ? prog[0]! : prog[i]!;
    const pad = c.pad.map(tr);
    const bells = f.bells(pad);
    const drop = b.name === 'drop' || b.name === 'drop2';
    const groove = b.name === 'A' || (b.name === 'outro' && !end);
    f.extra?.(x, { ...b, i, end, chord: c, pad, bells, drop, groove });

    if (end) {
      hit('kick', b.bar, 0, 0.95);
      hit('impact', b.bar, 0, 0.8);
      hit('crash', b.bar, 0, 0.7);
      chord('pad', b.bar, 0, pad, g.barLength * 1.2, 0.85, 0.02);
      chord('bell', b.bar, 0, bells.slice(0, pad.length), g.barLength, 0.7, 0.06);
      play('sub', b.bar, 0, tr(c.root), g.barLength * 0.9, 0.9);
      continue;
    }

    chord('pad', b.bar, 0, pad, g.barLength * 0.98, b.name === 'intro' ? 0.75 : 0.85, 0.015);

    if (drop || groove) {
      for (const h of hits(drop ? f.kick.drop : f.kick.groove)) hit('kick', b.bar, h.step, human(h.vel));
      for (const h of hits(drop ? f.back.drop : f.back.groove)) hit('snare', b.bar, h.step, human(f.back.vel[drop ? 1 : 0]));
      for (const h of hits(drop ? hatP : f.hats[0]!)) hit('hat', b.bar, h.step, human(h.vel * 0.85));
      if (drop && e >= f.open.from) for (const h of hits(f.open.pattern)) hit('open', b.bar, h.step, human(0.7));
      for (const [st, semis, len, vel] of drop ? f.bass.drop : f.bass.groove) play('sub', b.bar, st, transpose(tr(c.root), semis), s * len, vel);
      const mask = drop ? f.arp.drop : f.arp.groove;
      let n = 0;
      for (let st = 0; st < 16; st++) {
        const m = mask[st];
        if (m === undefined || m === '.') continue;
        const k = arp[n % arp.length]!;
        n++;
        play('bell', b.bar, st, bells[k]!, s * f.arp.len, human(m === 'X' ? 0.8 : 0.6));
      }
    } else if (b.name === 'break') {
      for (let st = 0; st < 16; st += 4) play('bell', b.bar, st, bells[arp[st / 4]!]!, s * 4, human(0.6));
      play('sub', b.bar, 0, tr(c.root), s * 15, 0.6);
      if (b.last) for (const h of hits('x-x-x-x-xxxxxxxx')) hit('hat', b.bar, h.step, 0.3 + (0.6 * h.step) / 16);
    } else if (b.name === 'intro' && b.index >= b.bars / 2) {
      for (let st = 0; st < 16; st += 4) play('bell', b.bar, st, bells[arp[st / 4]!]!, s * 4, human(0.5));
    }

    const row = hook[i]!;
    const lead = (vel: number, octave: boolean) => {
      for (const [step, note, len, v] of row) {
        play('lead', b.bar, step, tr(note), len * s * 0.95, human((v ?? 0.95) * vel));
        if (f.harm !== undefined) play('lead2', b.bar, step, tr(below(note, f.scale, f.harm)), len * s * 0.95, (v ?? 0.95) * vel * 0.75);
        if (octave) play('bell', b.bar, step, transpose(tr(note), 12), len * s, (v ?? 0.9) * 0.45);
      }
    };
    if (b.name === 'drop') lead(1, e >= 0.85);
    if (b.name === 'drop2') lead(1, e >= 0.6);
    if (b.name === 'break' && b.index < b.bars / 2) lead(0.7, false);
  }

  events.push({ v: 'rise', t: g.at(Math.max(intro, drop1 - 2)), end: g.at(drop1) - s * 2, bar: Math.max(intro, drop1 - 2), step: 0, vel: 1 });
  if (drop2 !== undefined && brk !== undefined) {
    const from = brk + sizeOf(form, 'break') / 2;
    events.push({ v: 'rise', t: g.at(from), end: g.at(drop2) - s * 2, bar: from, step: 0, vel: 1 });
    events.push({ v: 'fall', t: g.at(brk), end: g.at(brk + 1), bar: brk, step: 0, vel: 1 });
  }
  for (const bar of [drop1, drop2]) {
    if (bar === undefined) continue;
    hit('crash', bar, 0, 0.85);
    hit('impact', bar, 0, 1);
  }
  f.post?.(x);

  return {
    id: trackId(tpl, seed, p),
    template: tpl.id,
    seed,
    params: p,
    title: ch.orig ? tpl.ref.title : titleOf(seed),
    genre: tpl.genre,
    bpm: p.bpm,
    swing: f.swing,
    lufs: f.lufs,
    key,
    form,
    mix: f.mix(p.bpm),
    events,
    autos,
    accents: f.accents,
  };
}

export function flavored(f: Flavor): Template {
  const tpl: Template = { id: f.id, genre: f.genre, bpm: f.bpm, ref: f.ref, score: (seed, p) => build(f, tpl, seed, p) };
  return tpl;
}

const CHORDS: Readonly<Record<string, Chord>> = {
  Cmaj9: { root: 'C2', pad: ['E3', 'B3', 'D4', 'G4'] },
  Am9: { root: 'A1', pad: ['G3', 'B3', 'C4', 'E4'] },
  Fmaj7: { root: 'F1', pad: ['E3', 'A3', 'C4', 'G4'] },
  Gsus: { root: 'G1', pad: ['D3', 'A3', 'C4', 'G4'] },
  Em7: { root: 'E2', pad: ['D3', 'G3', 'B3', 'E4'] },
  Dm9: { root: 'D2', pad: ['F3', 'A3', 'C4', 'E4'] },
};

const HOOK: Note[][] = [
  [[0, 'E5', 3], [3, 'G5', 3], [6, 'A5', 4], [10, 'G5', 2], [12, 'E5', 4]],
  [[0, 'D5', 3], [3, 'G5', 3], [6, 'B5', 4], [10, 'A5', 2], [12, 'G5', 4]],
  [[0, 'B4', 3], [3, 'E5', 3], [6, 'G5', 6], [12, 'D5', 2], [14, 'E5', 2]],
  [[0, 'C5', 4], [4, 'E5', 2], [6, 'B5', 6], [12, 'A5', 4]],
];

export const KEYS = [0, 2, -3, -1, 3, -2, 1] as const;
export const MAJOR = [0, 2, 4, 5, 7, 9, 11] as const;
const BACK = '....x.......x...';
const SUB: Bass = [
  [0, 0, 7.5, 0.9],
  [8, 0, 7.5, 0.85],
];

export const DREAM = flavored({
  id: 'dream',
  genre: 'dream pop / chill electronica',
  bpm: [110, 128],
  ref: { seed: 'orbita', title: 'Órbita', params: { bpm: 118, bars: 28, energy: 0.55 } },
  chords: CHORDS,
  progs: [
    ['Fmaj7', 'Gsus', 'Em7', 'Am9'],
    ['Cmaj9', 'Am9', 'Fmaj7', 'Gsus'],
    ['Am9', 'Fmaj7', 'Cmaj9', 'Gsus'],
    ['Fmaj7', 'Em7', 'Dm9', 'Cmaj9'],
  ],
  hook: HOOK,
  arps: [
    [0, 1, 2, 3, 2, 1, 2, 3],
    [0, 2, 1, 3, 0, 2, 1, 3],
    [3, 2, 1, 0, 1, 2, 3, 2],
  ],
  keys: KEYS,
  scale: MAJOR,
  range: [69, 84],
  center: 76,
  plan: DREAM_PLAN,
  swing: 56,
  lufs: -11,
  mix: (bpm) => ({ reverbDecay: 3.6, delayTime: (60 / bpm) * 0.75, delayFeedback: 0.36, drive: 1.2, softness: 1.4, pumpDepth: 0.3, pumpRelease: 0.35 }),
  kick: { groove: 'X.......X.......', drop: 'X...X...X...X...' },
  back: { groove: BACK, drop: BACK, vel: [0.75, 0.92] },
  hats: ['..x...x...x...x.', 'x-x-x-x-x-x-x-x-', '-.x-.-x-.-x-.-xx'],
  open: { pattern: '..x...x...x...x.', from: 0.7 },
  bass: { groove: SUB, drop: SUB },
  arp: { groove: 'X.x.X.x.X.x.X.x.', drop: 'XxxxXxxxXxxxXxxx', len: 2.5 },
  bells: (pad) => pad.map((n) => transpose(n, 12)),
  accents: { kick: 'kick', snare: 'snare', pad: 'stab', lead: 'melody', impact: 'drop' },
});
